import { UploadStatus } from "@/shared/enums/upload-status";
import { StreamingService } from "@/shared/services/streamingService";
import store from "@/store";
import { artifactUploaderActions } from "@/store/artifactUploader/artifactUploaderAction";
import {
	determineStreamingStatus,
	dispatchProfilingEvent,
	updateCellToProfiledState,
} from "./streamingStatusHelpers";

const profilingStatusCache = new Set<string>();
// Control de los ficheros que ya fueron completados/fallidos
const completedStreamings = new Set<string>();

const POLLING_INTERVAL = 5000;
let pollingIntervalId: ReturnType<typeof setInterval> | null = null;
const streamingsToCheck = new Map<
	string,
	{ artifactId: string; fileName: string; datasetId: string }
>();

const syncStreamingsFromRedux = () => {
	const state = store.getState();
	const { artifactsProcessed, activeUploads } = state.uploadFile;

	for (const artifact of artifactsProcessed) {
		if (completedStreamings.has(artifact.streamingId || "")) {
			continue;
		}

		const isActive = activeUploads.some(
			(upload) =>
				upload.fileName === artifact.fileName &&
				upload.datasetId === artifact.datasetId &&
				upload.status === UploadStatus.ANALYZING,
		);

		if (
			isActive &&
			artifact.streamingId &&
			!streamingsToCheck.has(artifact.streamingId)
		) {
			streamingsToCheck.set(artifact.streamingId, {
				artifactId: artifact.artifactId,
				fileName: artifact.fileName,
				datasetId: artifact.datasetId,
			});
		}
	}
};

/**
 * Procesa un streaming individual y actualiza su estado
 */
const processStreaming = async (
	streamingId: string,
	artifactId: string,
	fileName: string,
	datasetId: string,
): Promise<boolean> => {
	try {
		const streamingData =
			await StreamingService.getStreamingStatus(streamingId);
		const statusResult = determineStreamingStatus(streamingData);

		// Si aún está procesando, continuar polling
		if (!statusResult.isCompleted && !statusResult.isFailed) {
			return false;
		}

		const identifier = artifactId || fileName;

		// Si ya fue procesado anteriormente, no hacer nada más
		if (profilingStatusCache.has(identifier)) {
			completedStreamings.add(streamingId);
			return true;
		}
		profilingStatusCache.add(identifier);

		// Actualizar estado en Redux
		updateReduxStore(fileName, datasetId, statusResult.finalStatus);

		// Actualizar elementos del DOM
		updateDOMElements(
			artifactId,
			statusResult.isCompleted,
			statusResult.errorMessage,
		);

		// Disparar eventos de completado
		dispatchCompletionEvents(
			fileName,
			artifactId,
			statusResult.isCompleted,
			statusResult.errorMessage,
		);

		completedStreamings.add(streamingId);

		return true;
	} catch (error) {
		completedStreamings.add(streamingId);
		return true;
	}
};

const updateReduxStore = (
	fileName: string,
	datasetId: string,
	status: UploadStatus,
): void => {
	if (datasetId && fileName) {
		store.dispatch(
			artifactUploaderActions.uploadStarted({
				id: fileName,
				fileName: fileName,
				status: status,
				datasetId: datasetId,
			}),
		);
	}
};

const updateDOMElements = (
	artifactId: string,
	isCompleted: boolean,
	errorMessage?: string,
): void => {
	if (!artifactId) return;

	const profilingCells = document.querySelectorAll(
		`.profiling-status-cell[data-artifact-id="${artifactId}"]`,
	);

	for (const cell of profilingCells) {
		updateCellToProfiledState(
			cell as HTMLElement,
			artifactId,
			isCompleted,
			errorMessage,
		);
	}
};

const dispatchCompletionEvents = (
	fileName: string,
	artifactId: string,
	success: boolean,
	errorMessage?: string,
): void => {
	if (fileName) {
		dispatchProfilingEvent("artifactProfilingCompletedByName", {
			fileName,
			artifactId,
			success,
			errorMessage,
		});
	}
};

const shouldStopPolling = (): boolean => {
	if (streamingsToCheck.size > 0) return false;

	const state = store.getState();
	const hasAnalyzingFiles = state.uploadFile.activeUploads.some(
		(upload) => upload.status === UploadStatus.ANALYZING,
	);

	return !hasAnalyzingFiles;
};

const checkStreamingStatuses = async () => {
	syncStreamingsFromRedux();

	const streamingsArray = Array.from(streamingsToCheck.entries());

	for (const [
		streamingId,
		{ artifactId, fileName, datasetId },
	] of streamingsArray) {
		const isProcessed = await processStreaming(
			streamingId,
			artifactId,
			fileName,
			datasetId,
		);

		if (isProcessed) {
			streamingsToCheck.delete(streamingId);
		}
	}

	if (shouldStopPolling()) {
		stopProfilingStatusPolling();
	}
};

export const addStreamingToCheck = (
	streamingId: string,
	artifactId: string,
	fileName: string,
	datasetId: string,
) => {
	if (!streamingId) return;

	streamingsToCheck.set(streamingId, { artifactId, fileName, datasetId });

	if (!pollingIntervalId) {
		pollingIntervalId = setInterval(checkStreamingStatuses, POLLING_INTERVAL);
		checkStreamingStatuses();
	}
};

export const startProfilingStatusPolling = () => {
	syncStreamingsFromRedux();

	if (streamingsToCheck.size > 0 && !pollingIntervalId) {
		pollingIntervalId = setInterval(checkStreamingStatuses, POLLING_INTERVAL);
		checkStreamingStatuses();
	} else if (streamingsToCheck.size > 0 && pollingIntervalId) {
		checkStreamingStatuses();
	}
};

export const stopProfilingStatusPolling = () => {
	if (pollingIntervalId) {
		clearInterval(pollingIntervalId);
		pollingIntervalId = null;
	}
	streamingsToCheck.clear();
	profilingStatusCache.clear();
	completedStreamings.clear();
};
